import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CreditCard,
  FileText,
  Settings,
  Check,
  AlertCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import api from "@/services/api";

// Billing mode configuration
const BILLING_MODES = {
  stripe: {
    label: "Stripe (Automatic)",
    description: "Automatic credit card billing via Stripe",
    icon: CreditCard,
    color:
      "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  },
  invoice: {
    label: "Invoice (Manual)",
    description: "Manual monthly invoicing",
    icon: FileText,
    color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  },
};

// Tax types
// Tax is DERIVED from the billing country; mirrors backend config/taxRates.js.
// The admin picks a country; the rate and label follow. They used to be three
// independent inputs and drifted immediately (billing_country 'US' with
// tax_type 'gst' at 10%).
const BILLING_COUNTRIES = [
  { value: "AU", label: "Australia", rate: 10, type: "GST" },
  { value: "NZ", label: "New Zealand", rate: 15, type: "GST" },
  { value: "GB", label: "United Kingdom", rate: 20, type: "VAT" },
  { value: "IE", label: "Ireland", rate: 21, type: "VAT" },
  { value: "DE", label: "Germany", rate: 21, type: "VAT" },
  { value: "FR", label: "France", rate: 21, type: "VAT" },
  { value: "NL", label: "Netherlands", rate: 21, type: "VAT" },
  { value: "US", label: "United States", rate: 0, type: "Sales Tax" },
];

const taxForCountry = (code) =>
  BILLING_COUNTRIES.find((c) => c.value === code) || {
    rate: 0,
    type: "No Tax",
  };

// Payment terms options
const PAYMENT_TERMS = [
  { value: 7, label: "Net 7" },
  { value: 14, label: "Net 14" },
  { value: 30, label: "Net 30" },
  { value: 45, label: "Net 45" },
  { value: 60, label: "Net 60" },
];

// Currency options
// AUD first: platform_pricing and config/plans.js are denominated in AUD, and
// picking another currency relabels invoices without converting any amount.
const CURRENCIES = [
  { value: "AUD", label: "AUD (A$)", symbol: "A$" },
  { value: "USD", label: "USD ($)", symbol: "$" },
  { value: "EUR", label: "EUR (€)", symbol: "€" },
  { value: "GBP", label: "GBP (£)", symbol: "£" },
  { value: "CAD", label: "CAD (C$)", symbol: "C$" },
  { value: "NZD", label: "NZD (NZ$)", symbol: "NZ$" },
  { value: "SGD", label: "SGD (S$)", symbol: "S$" },
  { value: "CHF", label: "CHF (Fr)", symbol: "Fr" },
  { value: "JPY", label: "JPY (¥)", symbol: "¥" },
  { value: "INR", label: "INR (₹)", symbol: "₹" },
];

export const BillingModeSettings = ({
  organisationId,
  organisationName,
  onUpdate,
}) => {
  const queryClient = useQueryClient();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingMode, setPendingMode] = useState(null);
  const [settings, setSettings] = useState({
    billingMode: "stripe",
    paymentTermsDays: 14,
    billingCountry: "AU",
    taxExempt: false,
    defaultCurrency: "AUD",
  });

  // Fetch current organisation billing settings
  const { data: orgData, isLoading } = useQuery({
    queryKey: ["admin", "organisation-billing-settings", organisationId],
    queryFn: async () => {
      const response = await api.get(
        `/admin/organisations/${organisationId}/billing-settings`,
      );
      return response.data?.data;
    },
    enabled: !!organisationId,
  });

  // Sync query data to local state (TanStack Query v5 pattern)
  useEffect(() => {
    if (orgData) {
      setSettings({
        billingMode: orgData.billing_mode || "stripe",
        paymentTermsDays: orgData.payment_terms_days || 14,
        billingCountry: orgData.billing_country || "AU",
        taxExempt: orgData.tax_exempt === true,
        defaultCurrency: orgData.default_currency || "AUD",
      });
    }
  }, [orgData]);

  // Fetch tax rates (for future use)
  useQuery({
    queryKey: ["admin", "tax-rates"],
    queryFn: async () => {
      const response = await api.get("/admin/tax-rates");
      return response.data?.data?.taxRates || [];
    },
  });

  // Update billing mode mutation
  const updateBillingMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.put(
        `/admin/organisations/${organisationId}/billing-mode`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success("Billing settings updated");
      queryClient.invalidateQueries({
        queryKey: ["admin", "organisation-billing-settings", organisationId],
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "organisations"] });
      setShowConfirmDialog(false);
      setPendingMode(null);
      onUpdate?.();
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message || "Failed to update billing settings",
      );
    },
  });

  // Handle billing mode change
  const handleModeChange = (newMode) => {
    if (newMode !== settings.billingMode) {
      setPendingMode(newMode);
      setShowConfirmDialog(true);
    }
  };

  // Confirm mode change
  const confirmModeChange = () => {
    updateBillingMutation.mutate({
      billingMode: pendingMode,
      paymentTermsDays: settings.paymentTermsDays,
      billingCountry: settings.billingCountry,
      taxExempt: settings.taxExempt,
      defaultCurrency: settings.defaultCurrency,
    });
  };

  // Save settings
  const saveSettings = () => {
    updateBillingMutation.mutate(settings);
  };

  if (isLoading) {
    return (
      <Card className="border-border">
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  const currentMode =
    BILLING_MODES[settings.billingMode] || BILLING_MODES.stripe;
  const CurrentIcon = currentMode.icon;

  return (
    <>
      <Card className="border-border">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Billing Settings
              </CardTitle>
              {organisationName && (
                <CardDescription className="mt-1">
                  Configure billing mode for {organisationName}
                </CardDescription>
              )}
            </div>
            <Badge className={currentMode.color}>
              <CurrentIcon className="h-3 w-3 mr-1" />
              {currentMode.label}
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Billing Mode Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Billing Mode</Label>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(BILLING_MODES).map(([mode, config]) => {
                const Icon = config.icon;
                const isSelected = settings.billingMode === mode;

                return (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => handleModeChange(mode)}
                    className={`relative flex flex-col items-start p-4 rounded-lg border-2 transition-all text-left ${
                      isSelected
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-muted-foreground/50 bg-card"
                    }`}
                  >
                    {isSelected && (
                      <div className="absolute top-2 right-2">
                        <Check className="h-5 w-5 text-primary" />
                      </div>
                    )}
                    <Icon
                      className={`h-8 w-8 mb-2 ${
                        isSelected ? "text-primary" : "text-muted-foreground"
                      }`}
                    />
                    <p
                      className={`font-medium ${
                        isSelected ? "text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {config.label}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {config.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Invoice-specific settings */}
          {settings.billingMode === "invoice" && (
            <div className="space-y-4 pt-4 border-t border-border">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileText className="h-4 w-4" />
                Invoice Settings
              </div>

              {/* Payment Terms */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">Payment Terms</Label>
                  <Select
                    value={String(settings.paymentTermsDays)}
                    onValueChange={(v) =>
                      setSettings((s) => ({
                        ...s,
                        paymentTermsDays: parseInt(v),
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_TERMS.map((term) => (
                        <SelectItem key={term.value} value={String(term.value)}>
                          {term.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Billing Country</Label>
                  <Select
                    value={settings.billingCountry}
                    onValueChange={(value) =>
                      setSettings((s) => ({ ...s, billingCountry: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BILLING_COUNTRIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.label} — {c.type} {c.rate}%
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {settings.taxExempt ? (
                      <>Tax exempt — invoices carry no tax.</>
                    ) : (
                      <>
                        Invoices carry {taxForCountry(settings.billingCountry).type}{" "}
                        at {taxForCountry(settings.billingCountry).rate}%. The rate
                        follows the country and cannot be set separately.
                      </>
                    )}
                  </p>
                  <label
                    className="flex items-center gap-2 text-sm pt-1"
                    htmlFor="tax-exempt"
                  >
                    <input
                      id="tax-exempt"
                      type="checkbox"
                      aria-label="Tax exempt"
                      checked={settings.taxExempt}
                      onChange={(e) =>
                        setSettings((s) => ({
                          ...s,
                          taxExempt: e.target.checked,
                        }))
                      }
                    />
                    Tax exempt
                  </label>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Default Currency</Label>
                  <Select
                    value={settings.defaultCurrency}
                    onValueChange={(value) =>
                      setSettings((s) => ({ ...s, defaultCurrency: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map((currency) => (
                        <SelectItem key={currency.value} value={currency.value}>
                          {currency.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* What the invoice will carry, derived from the country above.
                  There is no rate input: a custom rate that disagrees with the
                  country is the defect this change removes. */}
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <span className="text-sm text-muted-foreground">
                  Tax on invoices
                </span>
                <span className="font-medium text-foreground">
                  {settings.taxExempt
                    ? "Exempt — 0%"
                    : `${taxForCountry(settings.billingCountry).type} ${taxForCountry(settings.billingCountry).rate}%`}
                </span>
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="flex justify-end pt-4">
            <Button
              onClick={saveSettings}
              disabled={updateBillingMutation.isPending}
            >
              {updateBillingMutation.isPending ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Change Billing Mode
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to change the billing mode from{" "}
              <strong>{BILLING_MODES[settings.billingMode]?.label}</strong> to{" "}
              <strong>{BILLING_MODES[pendingMode]?.label}</strong>?
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {pendingMode === "invoice" ? (
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>Switching to invoice billing will:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Stop automatic Stripe charges</li>
                  <li>Enable detailed usage tracking</li>
                  <li>Require manual invoice generation</li>
                </ul>
              </div>
            ) : (
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>Switching to Stripe billing will:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Enable automatic credit card billing</li>
                  <li>Require a payment method on file</li>
                  <li>Process charges automatically</li>
                </ul>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmModeChange}
              disabled={updateBillingMutation.isPending}
            >
              {updateBillingMutation.isPending
                ? "Updating..."
                : "Confirm Change"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BillingModeSettings;
